import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { registerServiceWorker } from "@/services/pushNotifications";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import BusinessListingPage from "@/pages/BusinessListingPage";
import BusinessDetailPage from "@/pages/BusinessDetailPage";
import UserDashboard from "@/pages/UserDashboard";
import SubmitBusinessPage from "@/pages/SubmitBusinessPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminIngestionPage from "@/pages/AdminIngestionPage";
import AdminBulkImport from "@/pages/AdminBulkImport";
import AdminBlogPage from "@/pages/AdminBlogPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import GigBoardPage from "@/pages/GigBoardPage";
import BountyDetailPage from "@/pages/BountyDetailPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import NotificationsPage from "@/pages/NotificationsPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

// Components
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import { FeatureFlagsProvider } from "@/hooks/useFeatureFlags";

// Remove Emergent badge if injected externally
const removeEmergentBadge = () => {
  const selectors = [
    '#emergent-badge',
    '[id*="emergent-badge"]',
    'a[href*="emergent.sh"][href*="utm_source"]'
  ];
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => el.remove());
  });
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Global Error Boundary - prevents blank pages from uncaught React render errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ background: '#050505', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'Inter, sans-serif', padding: '2rem', textAlign: 'center' }}>
          <img src="/logo192.png" alt="Blackified" style={{ width: 80, marginBottom: 24, opacity: 0.8 }} />
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ color: '#a1a1aa', marginBottom: 24, maxWidth: 400 }}>We're having trouble loading the page. Please try refreshing.</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ background: '#fff', color: '#050505', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem' }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - SYNCHRONOUSLY during render
  // This prevents race conditions with auth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/businesses" element={<BusinessListingPage />} />
      <Route path="/businesses/:businessId" element={<BusinessDetailPage />} />
      <Route path="/bounties" element={<GigBoardPage />} />
      <Route path="/bounties/:bountyId" element={<BountyDetailPage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/submit-business" 
        element={
          <ProtectedRoute>
            <SubmitBusinessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/ingestion" 
        element={
          <AdminRoute>
            <AdminIngestionPage />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/bulk-import" 
        element={
          <AdminRoute>
            <AdminBulkImport />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/blog" 
        element={
          <AdminRoute>
            <AdminBlogPage />
          </AdminRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const API = `${window.location.origin}/api`;

/**
 * iOS PWA session restore via server-side nonce relay.
 *
 * Flow: The PWA stored a nonce before redirecting to Google auth.
 * After auth completed in Safari, the callback saved the session on the
 * server keyed by that nonce. Now that the user is back in the PWA,
 * we claim the session using the nonce.
 */
async function restorePwaSession() {
  try {
    const { getPwaNonce, clearPwaNonce, saveSessionToken } = await import('@/utils/authToken');
    const nonce = getPwaNonce();
    if (!nonce) return;

    const res = await fetch(`${API}/auth/pwa-auth-claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ nonce }),
    });

    if (res.ok) {
      const data = await res.json();
      saveSessionToken(data.session_token);
      clearPwaNonce();
      // Session cookie is now set – reload so the app recognises the user
      window.location.replace('/dashboard');
    } else {
      // Nonce may have expired or been claimed already
      clearPwaNonce();
    }
  } catch (e) {
    console.error('[PWA Auth Bridge] Error restoring session:', e);
  }
}

function App() {
  // Register service worker on app load and remove external badges
  useEffect(() => {
    registerServiceWorker().then((registration) => {
      if (registration) {
        console.log('Service worker registered for push notifications');
      }
    });
    
    // Remove Emergent badge on load and watch for dynamic injection
    removeEmergentBadge();
    const observer = new MutationObserver(() => removeEmergentBadge());
    observer.observe(document.body, { childList: true, subtree: true });

    // ── iOS PWA session bridge ──────────────────────────────────────────
    // Check immediately (app may have been backgrounded while Safari finished auth)
    restorePwaSession();

    // Also check whenever the PWA becomes visible again
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        restorePwaSession();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <FeatureFlagsProvider>
          <div className="min-h-screen bg-[#050505]">
            <BrowserRouter>
              <AppRouter />
              <Toaster 
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#0F0F0F',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#FFFFFF',
                  },
                }}
              />
            </BrowserRouter>
          </div>
        </FeatureFlagsProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
